import React, { forwardRef, useMemo } from "react"
import clsx from "clsx"
import { IconButtonSize, IconButtonVariant, useIconButtonStyles } from "./useIconButtonStyles"
import { Spinner } from "../Spinner"

interface Props {
  id?: string
  onClick?: (e: React.MouseEvent<HTMLDivElement, MouseEvent> | React.KeyboardEvent<HTMLDivElement>) => void
  disabled?: boolean
  Icon: React.ComponentType<React.SVGProps<SVGSVGElement>>
  floating?: boolean
  size?: IconButtonSize
  shape?: "circle" | "rounded"
  iconType?: "outline" | "filled"
  variant?: IconButtonVariant
  selected?: boolean
  loading?: boolean
  className?: string
  iconClassName?: string
}

export const IconButton = forwardRef<HTMLDivElement, Props>(
  (
    {
      id,
      Icon,
      onClick,
      loading = false,
      disabled = false,
      iconType = "outline",
      variant = "white",
      shape = "circle",
      size = "medium",
      selected = false,
      floating = false,
      iconClassName,
      className,
      ...rest
    },
    ref
  ) => {
    const { backgroundColor, iconColor, hover, border, _size } = useIconButtonStyles({
      variant,
      size,
      selected,
      iconType,
    })

    let iconSize
    switch (size) {
      case "xs":
        iconSize = 16
        break
      case "small":
        iconSize = 18
        break
      default:
        iconSize = 22
        break
    }

    const style = useMemo(() => {
      let styles = {}

      if (floating) {
        styles = { ...styles, boxShadow: "0px 8px 25px 0px rgba(0, 0, 0, 0.20)" }
      }

      if (variant === "blur") {
        styles = { ...styles, background: "rgba(0, 0, 0, 0.38)", backdropFilter: "blur(22.5px)" }
      }

      return styles
    }, [floating, variant])

    return (
      <div
        ref={ref}
        id={id}
        onClick={disabled ? () => null : onClick}
        style={style}
        className={clsx(
          "flex flex-shrink-0 cursor-pointer flex-row items-center justify-center border transition-colors duration-200 ease-in-out",
          _size,
          hover && `${hover}`,
          Boolean(border) && border,
          shape === "circle" ? "rounded-full" : size === "xs" ? "rounded-sm" : "rounded-lg",
          variant === "blur" && "backdrop-blur-sm",
          backgroundColor,
          className
        )}
        {...rest}>
        {loading ? (
          <Spinner size="small" className={iconColor} />
        ) : (
          <Icon height={iconSize} width={iconSize} className={clsx("flex flex-shrink-0", iconColor, iconClassName)} />
        )}
      </div>
    )
  }
)

IconButton.displayName = "IconButton"
